<template>
  <b-modal ref="modal"
           title="สร้างการ์ดใบเสนอราคา"
           size="lg"
           no-close-on-backdrop
           @hidden="resetState">
    <div class="row">
      <div class="col-12 col-lg-6">
        <label class="mb-1">ข้อความนำ</label>
        <b-form-textarea rows="2"
                         v-model="introText"
                         :placeholder="placeholders.introText"></b-form-textarea>

        <label class="mb-1 mt-3">ข้อความหลัก (บริการและราคา)</label>
        <b-form-textarea rows="4"
                         v-model="heroText"
                         :placeholder="placeholders.heroText"></b-form-textarea>

        <label class="mb-1 mt-3">ข้อความของแถม</label>
        <b-form-textarea rows="2"
                         v-model="bonusText"
                         :placeholder="placeholders.bonusText"></b-form-textarea>
        <div class="pt-2">
          <small class="text-muted d-block mt-1">
            ปุ่มทั้ง 3 ปุ่มจะถูกแนบไปกับการ์ดโดยอัตโนมัติ ไม่ต้องพิมพ์ในข้อความ
          </small>
          <small class="text-danger d-block mt-1" v-if="!isLineConversation">
            การ์ดใบเสนอราคาส่งได้เฉพาะแชท LINE เท่านั้น
          </small>
        </div>
      </div>
      <div class="col-12 col-lg-6 mt-3 mt-lg-0">
        <label class="mb-1">ตัวอย่างการ์ด</label>
        <!-- Mirrors how the flex bubble renders in the LINE chat: intro line,
             the service/price line as the hero, the bonus line, then the reply
             buttons stacked below. -->
        <div class="quote-card-preview">
          <div class="quote-card">
            <div class="quote-card-intro" :class="{ 'is-placeholder': !introText }">{{ introText || placeholders.introText }}</div>
            <div class="quote-card-hero" :class="{ 'is-placeholder': !heroText }">{{ heroText || placeholders.heroText }}</div>
            <div class="quote-card-bonus" :class="{ 'is-placeholder': !bonusText }">{{ bonusText || placeholders.bonusText }}</div>
            <div class="quote-card-buttons">
              <div v-for="(button, index) in buttons"
                   :key="index"
                   class="quote-card-button"
                   :class="button.accent ? 'quote-card-button--accent' : 'quote-card-button--plain'">
                {{ button.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template v-slot:modal-footer>
      <b-button variant="secondary" :disabled="isSending" @click="close">ยกเลิก</b-button>
      <b-button variant="success" :disabled="!canSend" @click="confirmSendQuoteCard">
        {{ isSending ? 'กำลังส่ง...' : 'ส่งการ์ด' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import axios from 'axios'

// Copy every card starts from. The hero is the three-tier price list — admins
// fill in the numbers and delete the tiers they are not offering. Used both for
// the initial state and by resetState, so reopening the modal starts clean.
const DEFAULT_TEXTS = {
  introText: 'จากข้อมูลที่คุณลูกค้าให้มา แอดมินขอเสนอ',
  heroText: [
    '🤍 Soft Clean ... บาท',
    '💙 Big Clean ... บาท',
    '💛 Deep Clean ... บาท'
  ].join('\n'),
  bonusText: ''
}

export default {
  name: 'QuoteCardModal',
  props: {
    selectedConversation: Object
  },
  data () {
    return {
      introText: DEFAULT_TEXTS.introText,
      heroText: DEFAULT_TEXTS.heroText,
      bonusText: DEFAULT_TEXTS.bonusText,
      isSending: false,
      // Sample copy, shown greyed out in the preview so the card layout is
      // readable before anything is typed.
      placeholders: {
        introText: 'จากข้อมูลที่คุณลูกค้าให้มา แอดมินขอเสนอ',
        heroText: 'Big Cleaning 4,250 บาท',
        bonusText: ''
      },
      // Labels the customer taps on the delivered card. Mirrors
      // QUOTE_CARD_BUTTONS in chat-server's helpers/quoteCardHelper.js so the
      // preview never drifts from what the card actually ships with.
      buttons: [
        { label: 'สนใจรับบริการ', code: 'interested', accent: true },
        { label: 'ยังไม่ทราบวันรับบริการ', code: 'no_date_yet' },
        { label: 'ยังไม่สนใจ', code: 'not_interested' }
      ]
    }
  },
  computed: {
    ...mapState({
      admin: state => state.admin.admin
    }),
    // The card is a LINE flex message — there is no equivalent on the other
    // channels, so the server rejects them too.
    isLineConversation () {
      return !!this.selectedConversation && this.selectedConversation.type === 'LineAtConversation'
    },
    canSend () {
      return !this.isSending && this.isLineConversation && !!this.heroText.trim()
    }
  },
  methods: {
    open () {
      this.$refs.modal.show()
    },
    close () {
      this.$refs.modal.hide()
    },
    resetState () {
      this.introText = DEFAULT_TEXTS.introText
      this.heroText = DEFAULT_TEXTS.heroText
      this.bonusText = DEFAULT_TEXTS.bonusText
      this.isSending = false
    },
    // Sending pushes the card straight to the customer's LINE chat and cannot
    // be undone, so make the admin proof-read the copy first.
    confirmSendQuoteCard () {
      this.$swal({
        text: 'กรุณาตรวจสอบเนื้อหาของการ์ดเสนอราคา ทั้งชื่อบริการและราคา ก่อนทำการกดยืนยันส่งให้ลูกค้าทราบ',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก'
      }).then(async (result) => {
        if (result.isConfirmed) {
          await this.sendQuoteCard()
        }
      })
    },
    async sendQuoteCard () {
      this.isSending = true
      try {
        const submitData = {
          conversation: this.selectedConversation,
          adminId: this.admin.id,
          introText: this.introText,
          heroText: this.heroText,
          bonusText: this.bonusText
        }
        const res = await axios.post('/messages/quote-card', submitData)
        if (res.status === 200) {
          this.close()
        }
      } catch (e) {
        console.log('check error', e.message)
        this.$swal({ text: 'ไม่สามารถส่งการ์ดใบเสนอราคาได้', icon: 'error' })
      }
      this.isSending = false
    }
  }
}
</script>

<style scoped lang="scss">
.quote-card-preview {
  background-color: #8CABD9;
  border-radius: 6px;
  padding: 16px;
}

.quote-card {
  background-color: #ffffff;
  border-radius: 10px;
  padding: 16px;
  max-width: 300px;
  margin: 0 auto;
}

.quote-card-intro,
.quote-card-hero,
.quote-card-bonus {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}

/* Matches the `md` margin the flex card gives every line after the first. */
.quote-card-intro + *,
.quote-card-hero + * {
  margin-top: 12px;
}

.quote-card-intro {
  color: #666666;
  font-size: 14px;
}

.quote-card-hero {
  color: #111111;
  font-size: 16px;
  font-weight: bold;
}

.quote-card-bonus {
  color: #333333;
  font-size: 14px;
}

.is-placeholder {
  color: #b0b0b0;
}

.quote-card-buttons {
  margin-top: 16px;
}

.quote-card-button {
  border-radius: 6px;
  font-size: 15px;
  height: 40px;
  line-height: 40px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 8px;

  & + & {
    margin-top: 10px;
  }
}

.quote-card-button--accent {
  background-color: #00C853;
  color: #ffffff;
}

.quote-card-button--plain {
  background-color: #DCDFE5;
  color: #333333;
}
</style>
